import { useEffect, useState } from "react";
import { Download, FileText } from "lucide-react";
import {
  Button,
  EmptyState,
  ListCard,
  SkeletonList,
  Timestamp,
} from "@/design-system/components/ui";
import { formatCurrency } from "@/design-system/lib/format";
import { PortalApiError } from "../api/client";
import { taxFormsApi, type TaxForm } from "../api/taxForms";

// TPS Servicing Portal PR-I (WS4) — the recipient's read-only "Tax forms" tab,
// shared by buyer / seller / broker / vendor. Lists only the forms a servicer
// has SENT to this account (Copy B), each downloadable as a PDF. No actions —
// the recipient cannot edit, approve, or create. No React Query in the portal;
// load is plain useState.

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof PortalApiError) return err.message;
  if (err instanceof Error) return err.message;
  return fallback;
}

/** Open the form's PDF in a new tab (Download / Print). */
async function openPdf(form: TaxForm): Promise<void> {
  const blob = await taxFormsApi.pdf(form.id);
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank", "noopener,noreferrer");
  // Revoke after a tick so the new tab has time to start loading the blob.
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export function RecipientTaxFormsTab() {
  const [rows, setRows] = useState<TaxForm[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await taxFormsApi.list();
        if (!cancelled) setRows(data);
      } catch (err) {
        if (!cancelled) setError(errorMessage(err, "Couldn't load your tax forms."));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return (
      <p className="text-small text-status-error" role="alert">
        {error}
      </p>
    );
  }

  if (!rows) {
    return <SkeletonList rows={3} data-testid="tax-forms-loading" />;
  }

  return (
    <section aria-label="Tax forms" className="flex flex-col gap-space-4">
      {rows.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="No tax forms yet"
          description="When a servicer issues you a 1099 information return, it will appear here for you to download and print."
        />
      ) : (
        <div className="flex flex-col gap-space-3">
          {rows.map((form) => (
            <TaxFormRow key={form.id} form={form} />
          ))}
        </div>
      )}
    </section>
  );
}

function TaxFormRow({ form }: { form: TaxForm }) {
  const [downloading, setDownloading] = useState(false);

  const download = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      await openPdf(form);
    } catch {
      /* the open is best-effort; a failed fetch leaves the tab as-is */
    } finally {
      setDownloading(false);
    }
  };

  return (
    <ListCard
      data-testid={`tax-form-row-${form.id}`}
      title={`${form.formTitle} — ${form.taxYear}`}
      meta={
        <span className="flex flex-wrap items-center gap-x-space-2 gap-y-space-1">
          <span>{formatCurrency(form.box1Amount)}</span>
          <span aria-hidden>·</span>
          <span>{form.box1Label}</span>
          {form.sentAt && (
            <>
              <span aria-hidden>·</span>
              <span>
                Issued <Timestamp value={form.sentAt} />
              </span>
            </>
          )}
        </span>
      }
      actions={
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => void download()}
          loading={downloading}
          disabled={downloading}
          data-testid={`tax-form-download-${form.id}`}
        >
          <Download size={16} aria-hidden />
          Download
        </Button>
      }
    />
  );
}
